import { useEffect, useState, useMemo } from "react";
import clsx from "clsx";
import { useReactTable, getCoreRowModel, flexRender, ColumnDef } from "@tanstack/react-table";
import { NumericFormat } from "react-number-format";
import ReactSlider from "react-slider";
import BigNumber from "bignumber.js";
import { AlertTriangle } from "react-feather";
import {
  formatBalance,
  fromHumanAmountToTokenBalance,
  humanizeAmount,
  maxDecimalsToDisplay,
} from "@axvdex/utils/formatNumber";
import unstakeSimulation, { hybridLPTokenRepresentation } from "@axvdex/utils/unstakeSimulation";
import {
  selectAssetBalances,
  selectAssets,
  selectChains,
  selectWalletInfo,
} from "@axvdex/state/wallet/walletSelectors";
import {
  executePoolAction,
  retryWalletWithDispatch,
  updateLpStakingBalance,
  updateNativeBalance,
  updateTokenBalance,
} from "@axvdex/state/wallet/walletThunks";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import Button from "@axvdex/components/common/Button";
//import stakeSimulation from "@axvdex/utils/swapScripts/stakeSimulation";
//import BigNumber from "bignumber.js";
import imgSanitize from "@axvdex/utils/imgSanitize";
import useLanguage from "@axvdex/hooks/useLanguage";
import { WHITE_LIST_PERSISTED_STATE_KEYS, loadState } from "@axvdex/state/persist";
import CustomNumericInput from "../form-element/CustomNumericInput";
import CustomLoader from "../common/CustomLoader";
import CustomSwitch from "../form-element/CustomSwitch";

// const DummyPoolCardDetailsTableList = [
//   { token: "ARCH", total: 10, deposits: 10 },
//   { token: "xARCH", total: 11, deposits: 15 },
// ];

interface PoolCardStandardWithdrawalTableProps {
  id: string;
  symbol: string;
  image: string;
  total: number;
  deposited: {
    amount: number;
    usdConversion: number;
  };
  input: string;
  balance: number | null;
}

interface ComponentProps {
  poolData: any;
  [key: string]: any;
}

function PoolCardHybridWithdrawalTable({ poolData }: ComponentProps) {
  const { i18 } = useLanguage();
  const assetBalances = useAppSelector(selectAssetBalances);
  const assets = useAppSelector(selectAssets);
  const chains = useAppSelector(selectChains);

  const [data, setData] = useState<PoolCardStandardWithdrawalTableProps[]>([]);
  const [simulationValues, setSimulationValues] = useState<any>(null);
  const [isTransactionStarts, setIsTransactionStarts] = useState(false);
  const [hasReconnectedChainContext, setHasReconnectedChainContext] = useState(false); // used when need to reconnect wallet to redo the useEffect that updates the initial state
  const [sliderValue, setSliderValue] = useState(0);
  const dispatch = useAppDispatch();
  const walletInfo = useAppSelector(selectWalletInfo);
  const [isInstantWithDrawal, setIsInstantWithDrawal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const walletChainContext = walletInfo?.connectedChains[poolData.contextChainId];

  useEffect(() => {
    update();
  }, [walletInfo.isConnected, assetBalances, hasReconnectedChainContext]);

  const update = async () => {
    // if there is no wallet connect and auto connect is enabled, we should show the loader as we are trying to auto connect
    if (!walletInfo.isConnected && loadState(WHITE_LIST_PERSISTED_STATE_KEYS.autoConnectWallet)) {
      setIsLoading(true);
      return;
    }
    if (0 === data.length) setIsLoading(true);
    // get asset balances of this pool that are not already in state
    const requests = [];
    for (const [i, poolAsset] of poolData.poolAssets.entries()) {
      if (!assetBalances[poolData.assetIDs[i]] && walletInfo.isConnected) {
        if (poolAsset.info.token) {
          requests.push(
            dispatch(
              updateTokenBalance({
                client: walletChainContext.signingClient,
                userAddress: walletChainContext.address,
                tokenAddress: poolAsset.info.token.contract_addr,
              })
            )
          );
        } else {
          requests.push(
            dispatch(
              updateNativeBalance({
                client: walletChainContext.signingClient,
                userAddress: walletChainContext.address,
                denom: poolAsset.info.native_token.denom,
              })
            )
          );
        }
      } else {
        requests.push(Promise.resolve());
      }
    }

    const poolAssetBalances = await Promise.all(requests);

    // get balance of this user on this pool
    let lpStakingBalance = assetBalances[poolData?.lp_staking];
    if (!assetBalances[poolData?.lp_staking] && walletInfo.isConnected) {
      lpStakingBalance = (
        await dispatch(
          updateLpStakingBalance({
            client: walletChainContext.signingClient,
            userAddress: walletChainContext.address,
            lpStakingAddress: poolData?.lp_staking,
          })
        )
      ).payload;
    }

    const pPoolData = poolData.poolAssets.map((asset, i) => {
      return {
        id: poolData?.assetIDs[i],
        total: humanizeAmount(asset.amount, assets[poolData?.assetIDs[i]]?.decimals),
        symbol: assets[poolData?.assetIDs[i]]?.symbol,
        deposited: formatBalance(
          hybridLPTokenRepresentation((lpStakingBalance as string) || "0", poolData).refund_assets[i] || "0",
          assets[poolData?.assetIDs[i]]?.price,
          assets[poolData?.assetIDs[i]]?.decimals
        ),
        input: "",
        balance: poolAssetBalances[i]
          ? formatBalance(
              poolAssetBalances[i].payload,
              assets[poolData?.assetIDs[i]].price,
              assets[poolData?.assetIDs[i]].decimals
            ).amount || 0
          : formatBalance(
              assetBalances[poolData?.assetIDs[i]],
              assets[poolData?.assetIDs[i]].price,
              assets[poolData?.assetIDs[i]].decimals
            ).amount || 0,
      };
    });
    setData(pPoolData);
    setIsLoading(false);
  };

  const handleWithdrawalSliderChange = lpAmount => {
    const transformData = unstakeSimulation(
      poolData,
      lpAmount,
      null,
      { is_instant_withdrawal: isInstantWithDrawal },
      walletChainContext.address,
      assetBalances[poolData?.lp_staking]
    );
    setData(prevData => {
      const newData = [...prevData];
      if (transformData.refund_assets) {
        newData[0].input = transformData.refund_assets[0];
        newData[1].input = transformData.refund_assets[1];
      }
      return newData;
    });
    setSimulationValues({ ...transformData });
    if ("0" === transformData.lockupFeePer) setIsInstantWithDrawal(true);
  };

  const handleIsInstantWithdrawalToogle = isInstantWithDrawal => {
    const transformData = unstakeSimulation(
      poolData,
      BigNumber(sliderValue).div(100).times(assetBalances[poolData.lp_staking]).toString(10),
      null,
      { is_instant_withdrawal: isInstantWithDrawal },
      walletChainContext.address,
      assetBalances[poolData?.lp_staking]
    );
    setData(prevData => {
      const newData = [...prevData];
      if (transformData.refund_assets) {
        newData[0].input = transformData.refund_assets[0];
        newData[1].input = transformData.refund_assets[1];
      }
      return newData;
    });
    setSimulationValues({ ...transformData });
  };

  // useEffect(() => {
  //   data.length &&handleWithdrawalSliderChange(0);
  // }, []);

  const reconnectWallet = async () => {
    setIsTransactionStarts(true);
    await dispatch(
      retryWalletWithDispatch({
        chainToConnect: chains[poolData.contextChainId],
      })
    );
    setHasReconnectedChainContext(!hasReconnectedChainContext);
    setIsTransactionStarts(false);
  };

  const handleSubmit = async () => {
    setIsTransactionStarts(true);

    await dispatch(
      executePoolAction({
        poolData,
        simulationValues,
        isDeposit: false,
        i18,
      })
    );

    setSliderValue(0);
    handleWithdrawalSliderChange("0");
    setIsTransactionStarts(false);
  };

  const defaultColumns = useMemo<ColumnDef<PoolCardStandardWithdrawalTableProps>[]>(() => {
    return [
      {
        id: "token",
        header: () => <span>{i18("Token Balance", "poolCardTable.table.columns.token")}</span>,
        cell: ({ row }) => (
          <div className="flexbox">
            <span className="tokenIcon" title={row.original.symbol} aria-label={row.original.symbol}>
              <img
                src={imgSanitize(row.original.symbol)}
                alt={row.original.symbol}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  dispatch(
                    updateTokenBalance({
                      client: walletChainContext.signingClient,
                      userAddress: walletChainContext.address,
                      tokenAddress: row.original.id,
                    })
                  )
                }
              />
            </span>

            {row.original.balance !== null && <span>{row.original.balance}</span>}
          </div>
        ),
      },
      {
        id: "deposited",
        accessorKey: "deposited",
        header: () => <span>{i18("Deposited", "poolCardTable.table.columns.deposited")}</span>,
        cell: ({ row }) => (
          <div className="flexbox deposited">
            <NumericFormat
              displayType="text"
              thousandSeparator=","
              decimalSeparator="."
              decimalScale={maxDecimalsToDisplay(BigNumber(row.original.deposited.amount || 0))}
              suffix=""
              value={BigNumber(row.original.deposited.amount || 0)
                .decimalPlaces(
                  maxDecimalsToDisplay(BigNumber(row.original.deposited.amount || 0)),
                  BigNumber.ROUND_FLOOR
                )
                .toString()}
            />

            <NumericFormat
              className="tableInputValueConversion"
              displayType="text"
              thousandSeparator=","
              decimalSeparator="."
              decimalScale={2}
              prefix={"($"}
              suffix={")"}
              value={BigNumber(row.original.deposited.usdConversion || 0)
                .decimalPlaces(2, BigNumber.ROUND_FLOOR)
                .toString()}
            />
          </div>
        ),
      },
      {
        id: "actions",
        header: () => <span className="visuallyHidden">{i18("Actions", "poolCardTable.table.columns.actions")}</span>,
        cell: ({ row }) => {
          return (
            <>
              <CustomNumericInput
                extraClassName="derivativeFromUsdFormGroup"
                name={`amount_${row.index}`}
                labelText={i18("Enter amount", "poolCardTable.input.label")}
                hiddenLabel={true}
                decimalScale={6}
                placeholder={"0"}
                value={row.original.input}
                disabled
              />

              <div className="tableInputValueConversion withMarginTop">
                ($
                {
                  formatBalance(
                    fromHumanAmountToTokenBalance(
                      row.original.input?.replaceAll(",", ""),
                      assets[row.original.id].decimals
                    ) || "0",
                    assets[row.original.id].price,
                    assets[row.original.id].decimals
                  ).usdConversion
                }
                )
              </div>
            </>
          );
        },
      },
    ];
  }, [i18]);

  const [columns, setColumns] = useState(() => [...defaultColumns]);

  useEffect(() => {
    setColumns([...defaultColumns]);
  }, [defaultColumns]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const submitButtonText = () => {
    const action = i18("Withdrawal", "poolCardTable.submitBtn.text.withdrawal");
    const chain = chains[poolData?.contextChainId]?.displayName;
    if (!walletInfo.connectedChains[poolData?.contextChainId]?.signingStargateClient) {
      return {
        buttonText: i18(`${chain} not connected. Try to reconnect...`, "poolCardTable.submitBtn.text.notConnected", {
          chain,
        }),
        disabled: false,
        reconnectWalletAction: true,
      };
    }

    if (data.every(value => !value.input || !Number(value.input))) {
      return {
        buttonText: action,
        disabled: true,
      };
    }

    if (!poolData?.owner_settings?.is_withdrawal_to_lockup_enabled) {
      return {
        buttonText: i18("Withdrawal disabled", "poolCardTable.submitBtn.text.disabled", { action }),
        disabled: true,
      };
    }

    if (isTransactionStarts) {
      return {
        buttonText: i18("Executing...", "poolCardTable.submitBtn.text.executing"),
        disabled: true,
      };
    }

    return {
      buttonText: action,
      disabled: false,
    };
  };

  // console.log(simulationValues);
  return (
    <section className={clsx("poolCardModalSectionTable withGradientBorderBottom")}>
      {isLoading ? (
        <div style={{ textAlign: "center", marginBottom: "1.5em" }}>
          {walletChainContext?.signingStargateClient && <CustomLoader size="xs" />}
        </div>
      ) : (
        <>
          <table
            className={clsx(
              "table",
              "withGradientBorderRows",
              "poolCardModalDetailsTable",
              "isResponsive",
              "col-3",
              "standardWithdrawal"
            )}
          >
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <CustomSwitch
                    extraClassName="StableWithdrawalCustomSwitch withGradientBorderBottom"
                    name="instant_withdrawal"
                    labelText={
                      <>
                        <p>{i18("Instant Withdraw", "poolCardTable.withdrawTable.instantWithdraw")}</p>
                        <small className="textGrey">
                          {i18(
                            "Additional charges are shown when selected",
                            "poolCardTable.withdrawTable.instantWithdraw.info"
                          )}
                        </small>
                      </>
                    }
                    isToggled={isInstantWithDrawal}
                    onToggle={e => {
                      setIsInstantWithDrawal(e.target.checked);
                      handleIsInstantWithdrawalToogle(e.target.checked);
                    }}
                  />

                  {simulationValues && isInstantWithDrawal && (
                    <div className="tableTFootFlexbox">
                      <small>
                        {i18("Lockup Fee Percentage", "poolCardTable.withdrawTable.footNote.lockupFeePercentage")}
                      </small>
                      <small>{simulationValues.lockupFeePer} %</small>
                    </div>
                  )}

                  {simulationValues && isInstantWithDrawal && (
                    <div className="tableTFootFlexbox flexStart withBorderBottom">
                      <small>
                        {i18("Assets Lockup Fee Amount", "poolCardTable.withdrawTable.footNote.assetsLockupFee")}
                      </small>
                      <span className="tableTFootColumn">
                        {simulationValues.assets_lockup_fee_amount.map((fee, index) => (
                          <small key={index}>
                            {fee} {assets[poolData.assetIDs[index]].symbol}
                          </small>
                        ))}
                      </span>
                    </div>
                  )}

                  {simulationValues && simulationValues.final_withdrawal_assets_amount_lockup.lenght > 0 && (
                    <div className="tableTFootFlexbox flexStart">
                      <small>{i18("Final Amount", "poolCardTable.withdrawTable.footNote.finalAmount")}</small>
                      <span className="tableTFootColumn">
                        {simulationValues.final_withdrawal_assets_amount_lockup.map((fee, index) => (
                          <span key={index}>
                            {fee} {assets[poolData.assetIDs[index]].symbol}
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
          <br />
          <ReactSlider
            className="horizontalSlider"
            thumbClassName="horizontalSliderThumb"
            trackClassName="horizontalSliderTrack"
            value={sliderValue}
            onChange={value => {
              const inputValue = BigNumber(value).div(100).times(assetBalances[poolData.lp_staking]).toString(10);
              if (undefined !== inputValue && null !== inputValue) {
                handleWithdrawalSliderChange(inputValue);
              }
              setSliderValue(value);
            }}
            renderThumb={(props, state) => (
              <div {...props}>
                <span className="thumbNowValue">{state.valueNow}%</span>
                <span className="thumbHandle" />
              </div>
            )}
          />
        </>
      )}

      {!isTransactionStarts ? (
        <>
          <Button
            text={submitButtonText().buttonText}
            title={i18("Submit", "poolCardTable.submitBtn.title")}
            isFullWidth={true}
            btnColor="gradient"
            onClick={submitButtonText().reconnectWalletAction ? reconnectWallet : handleSubmit}
            disabled={submitButtonText().disabled}
          />
          {simulationValues &&
            assetBalances &&
            poolData &&
            simulationValues.lpTokensAmount === assetBalances[poolData?.lp_staking] && (
              <>
                <div className="flexbox helpText">
                  <span className="warning tokenIcon">
                    <AlertTriangle />
                  </span>
                  <span>
                    {i18(
                      `Withdrawing the full amount from a pool will automatically claim it's accumulated rewards`,
                      "poolCardTable.withdrawTable.helpText"
                    )}
                  </span>
                </div>
              </>
            )}
        </>
      ) : (
        <CustomLoader text={i18("Processing...", "poolCardTable.loader.text")} size="xs" />
      )}
    </section>
  );
}

export default PoolCardHybridWithdrawalTable;
